'use client';

import Image from 'next/image';
import { Check, X } from 'phosphor-icons';
import * as React from 'react';
import { useEffect, useLayoutEffect } from 'react';
import { c } from '../../../utils/tailwind-merge';
import { Button } from '../../atoms/Button/Button';
import { EntitlementConfirmationProps } from './EntitlementConfirmation.types';
import { useEntitlementConfirmation } from './useEntitlementConfirmation';

export const EntitlementConfirmation = ({
  type,
  productName,
  productImage,
  microcopy,
  onGoToMyAssets,
  onOpenInUnityFromEntitlement,
  onSeeOwnedPackages,
  onCloseFromEntitlement,
  className = '',
}: EntitlementConfirmationProps) => {
  const { isOpen, open, close } = useEntitlementConfirmation();

  const dialogRef = React.useRef<HTMLElement | null>(null);
  const closeButtonRef = React.useRef<HTMLButtonElement | null>(null);
  const previouslyFocusedElementRef = React.useRef<HTMLElement | null>(null);

  useLayoutEffect(() => {
    open();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('overflow-hidden');
    }
    if (!isOpen) {
      // Restore focus to the element that was focused before the dialog opened.
      previouslyFocusedElementRef.current?.focus();
    } else {
      (
        previouslyFocusedElementRef as React.MutableRefObject<HTMLElement | null>
      ).current = (document.activeElement as HTMLElement) || null;
      // Move focus into the dialog when it opens.
      if (closeButtonRef.current) {
        closeButtonRef.current.focus();
      } else if (dialogRef.current) {
        const focusableSelectors =
          'a[href], button:not([disabled]), textarea, input[type="text"], input[type="email"], input[type="search"], input[type="url"], input[type="password"], select, [tabindex]:not([tabindex="-1"])';
        const focusable =
          dialogRef.current.querySelectorAll<HTMLElement>(focusableSelectors);
        if (focusable.length > 0) {
          focusable[0].focus();
        }
      }
    }
    return () => {
      document.body.classList.remove('overflow-hidden');
    };
  }, [isOpen]);

  const handleClose = () => {
    close();
    onCloseFromEntitlement();
  };

  if (!isOpen) return null;

  return (
    <>
      <section
        role="dialog"
        aria-modal="true"
        aria-label={microcopy.addedToMyAsset}
        data-test="entitlement-confirmation-section"
        ref={dialogRef as React.RefObject<HTMLElement>}
        aria-live="polite"
        className={c(
          'z-modal fixed inset-x-0 top-0 w-full bg-grey-800 py-14 text-white',
          className
        )}
      >
        <button
          ref={closeButtonRef}
          type="button"
          aria-label="Close"
          onClick={handleClose}
          className="absolute right-4 top-4"
          data-test="entitlement-confirmation-close"
        >
          <X width={24} height={24} />
        </button>

        <div className="lg-container flex flex-col gap-4 md:flex-row md:items-center md:gap-6">
          <div className="flex shrink-0 items-center gap-2">
            <span className="body-small">{microcopy.addedToMyAsset}</span>
            <span
              aria-hidden="true"
              className="bg-emerald-500 inline-flex h-6 w-6 items-center justify-center rounded-full"
            >
              <Check width={24} height={24} />
            </span>
          </div>

          <div className="flex min-w-0 items-center gap-3 md:gap-4">
            <Image
              src={productImage}
              alt=""
              className="h-16 w-24 rounded-lg object-cover"
              width={110}
              height={74}
            />
            <h3 className="body-small min-w-0 max-w-64">{productName}</h3>
          </div>

          <div className="mt-1 flex items-center gap-3 md:ml-auto [&_span]:uppercase [&_span]:tracking-[0.0875rem]">
            <Button
              type="button"
              onClick={onGoToMyAssets}
              label={microcopy.goToMyAsset}
              size="tiny"
              style="secondaryMonochrome"
              edge="rounded"
              isDarkMode
              role="link"
            />

            {type === 'multi-package' ? (
              <Button
                type="button"
                onClick={onSeeOwnedPackages}
                label={microcopy.seeOwnedPackages}
                size="tiny"
                style="whiteBg"
                edge="rounded"
                isDarkMode
                role="link"
              />
            ) : (
              <Button
                type="button"
                onClick={onOpenInUnityFromEntitlement}
                label={microcopy.openInUnity}
                size="tiny"
                style="whiteBg"
                edge="rounded"
                isDarkMode
              />
            )}
          </div>
        </div>
      </section>
      <div className="pointer-events-auto fixed inset-0 z-10 bg-black/60"></div>
    </>
  );
};
